import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Layout from '../components/layout/Layout';
import Seo from '../components/common/Seo';
import { dishes, formatPrice } from '../data/dishes';
import { SITE_URL, business } from '../data/business';

const easing: [number, number, number, number] = [0.25, 0.1, 0.25, 1];

/**
 * Hub for the signature dish pages — the restaurant equivalent of a services
 * hub. It links to every dish page, and every dish page links back.
 */
const DishesPage = () => {
  const itemListSchema = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: 'Signature dishes at Thaprachan Thai Cuisine',
    itemListElement: [
      ...dishes.map((dish, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        name: dish.name,
        url: `${SITE_URL}/dishes/${dish.slug}`,
      })),
      {
        '@type': 'ListItem',
        position: dishes.length + 1,
        name: 'Thai Street Food',
        url: `${SITE_URL}/thai-street-food-seattle`,
      },
      {
        '@type': 'ListItem',
        position: dishes.length + 2,
        name: 'Vegetarian Thai Food',
        url: `${SITE_URL}/vegetarian-thai-seattle`,
      },
    ],
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
      { '@type': 'ListItem', position: 2, name: 'Signature Dishes', item: `${SITE_URL}/dishes` },
    ],
  };

  return (
    <>
      <Seo
        title="Signature Thai Dishes in Seattle | Thaprachan Thai"
        description="Pad thai, khao soi, pad see ew, drunken noodles and mango sticky rice at Thaprachan Thai Cuisine, 805 NE 65th St in north Seattle. Order online."
        path="/dishes"
        schemas={[itemListSchema, breadcrumbSchema]}
      />

      <Layout>
        <section className="relative bg-black overflow-hidden">
          <div className="absolute inset-0 z-0">
            <img
              src="/assets/images/Hero Background Photos/optimized/IMG_8254.webp"
              alt=""
              className="w-full h-full object-cover object-center"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/70 to-black/80" />
          </div>

          <div className="relative z-10 container mx-auto px-6 lg:px-8 py-20 md:py-28">
            <motion.p
              className="text-sm tracking-widest uppercase text-primary/90 font-medium font-body mb-4"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: easing }}
            >
              Dish by dish
            </motion.p>
            <motion.h1
              className="font-heading text-4xl sm:text-5xl md:text-6xl text-white leading-tight max-w-4xl"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.12, ease: easing }}
            >
              Signature Thai Dishes in Seattle
            </motion.h1>
            <motion.p
              className="font-body text-lg text-white/85 max-w-2xl mt-6"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.25 }}
            >
              Five dishes people come to Thaprachan for by name — what goes into each one,
              what it costs, and how to order it.
            </motion.p>
          </div>
        </section>

        <div className="bg-background bg-paper-texture">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
            <div className="max-w-3xl">
              <p className="font-body text-text/90 leading-relaxed">
                Our{' '}
                <Link to="/menu" className="text-primary-ink underline underline-offset-2 hover:text-secondary">
                  full menu
                </Link>{' '}
                runs to eleven sections — noodles, curries, fried rice, salads, Thai street food,
                desserts and more. The five dishes below have pages of their own because they are
                the ones guests ask for by name.
              </p>
            </div>

            <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {dishes.map((dish, i) => (
                <motion.article
                  key={dish.slug}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.2 }}
                  transition={{ duration: 0.5, delay: Math.min(i * 0.06, 0.3), ease: 'easeOut' }}
                  className="bg-white rounded-sm shadow-elegant p-6 flex flex-col"
                >
                  <p className="font-body text-xs tracking-widest uppercase text-primary-ink mb-2">
                    {dish.menuSection}
                  </p>
                  <h2 className="font-heading text-2xl text-secondary">
                    <Link to={`/dishes/${dish.slug}`} className="hover:text-primary-ink transition-colors">
                      {dish.name}
                    </Link>
                  </h2>
                  <p className="font-body text-sm text-text/60 mt-1">
                    {dish.thaiScript} · {dish.romanization}
                  </p>
                  <p className="font-body text-sm text-text/85 leading-relaxed mt-4 flex-grow">
                    {dish.menuDescription}
                  </p>
                  <div className="flex items-baseline justify-between mt-5 pt-4 border-t border-neutral-warm">
                    <span className="font-body font-semibold text-primary-ink tabular-nums">
                      {formatPrice(dish.basePrice)}
                    </span>
                    <Link
                      to={`/dishes/${dish.slug}`}
                      className="font-body text-xs tracking-widest uppercase text-secondary hover:text-primary-ink transition-colors"
                    >
                      Read more →
                    </Link>
                  </div>
                </motion.article>
              ))}
            </div>

            <section className="mt-16">
              <h2 className="font-heading text-2xl md:text-3xl text-secondary">
                Looking for something in particular
              </h2>
              <div className="mt-3 h-0.5 w-12 bg-primary" />

              <div className="mt-6 grid gap-4 sm:grid-cols-2">
                <Link
                  to="/thai-street-food-seattle"
                  className="block bg-white rounded-sm shadow-elegant p-6 hover:shadow-premium transition-shadow"
                >
                  <p className="font-body text-xs tracking-widest uppercase text-primary-ink mb-2">
                    14 dishes
                  </p>
                  <h3 className="font-heading text-xl text-secondary mb-2">Thai Street Food</h3>
                  <p className="font-body text-sm text-text/80 leading-relaxed">
                    Som tum tard, pork belly skewers, moo nam tok, pad kraprow under a fried
                    egg — a section most Thai menus in Seattle don&rsquo;t have.
                  </p>
                </Link>

                <Link
                  to="/vegetarian-thai-seattle"
                  className="block bg-white rounded-sm shadow-elegant p-6 hover:shadow-premium transition-shadow"
                >
                  <p className="font-body text-xs tracking-widest uppercase text-primary-ink mb-2">
                    Tofu &amp; vegetables
                  </p>
                  <h3 className="font-heading text-xl text-secondary mb-2">Vegetarian Thai Food</h3>
                  <p className="font-body text-sm text-text/80 leading-relaxed">
                    Which dishes are built on vegetables and tofu, which let you choose the
                    protein, and what to ask about before you order.
                  </p>
                </Link>
              </div>
            </section>

            <div className="max-w-3xl mt-14">
              <div className="bg-white rounded-sm shadow-elegant p-6 md:p-8">
                <h2 className="font-heading text-2xl text-secondary mb-3">Order or visit</h2>
                <p className="font-body text-text/85 leading-relaxed">
                  Thaprachan Thai Cuisine is at 805 NE 65th St, Seattle, WA 98115, about a
                  10-minute drive from the{' '}
                  <Link to="/near-uw" className="text-primary-ink underline underline-offset-2 hover:text-secondary">
                    University of Washington
                  </Link>
                  .
                </p>
                <div className="flex flex-wrap gap-4 mt-6">
                  <a
                    href={business.orderUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-track="order_click"
                    className="btn-primary rounded-sm"
                  >
                    Order Online →
                  </a>
                  <a
                    href={business.telephoneHref}
                    data-track="call_click"
                    className="px-8 py-3 border border-secondary/40 text-secondary font-body font-medium rounded-sm hover:bg-secondary/5 transition-colors"
                  >
                    Call {business.telephoneDisplay}
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Layout>
    </>
  );
};

export default DishesPage;
